import React, { useState, useEffect, useCallback } from 'react';
import Axios from 'axios';
import Swal from 'sweetalert2';
import config from './config';

/**
 * Panel de la votación diaria de colaciones que llega desde el bot de WhatsApp.
 *
 * Muestra tres cosas que conviene no confundir entre sí:
 *
 *  - El estado de la ventana de votación. Hasta las 09:30 la gente todavía
 *    puede cambiar su voto, así que lo que se ve es preliminar y no está
 *    guardado. Después del cierre, lo guardado es lo definitivo.
 *  - Los votos que no cruzaron con ningún trabajador. Nunca se descartan solos:
 *    quedan aquí hasta que una persona los resuelva.
 *  - Quién no votó, que no es lo mismo que haber votado "No almuerza".
 */
function ColacionesBot({ idEmpresa, userId, usuario, trabajadores, onCambio }) {
    const hoy = new Date().toLocaleDateString('en-CA');

    const [fecha, setFecha] = useState(hoy);
    const [dia, setDia] = useState(null);
    const [pendientes, setPendientes] = useState([]);
    const [estadoBot, setEstadoBot] = useState({ disponible: null, cutoff: '09:30' });
    const [cargando, setCargando] = useState(false);
    const [sincronizando, setSincronizando] = useState(false);
    const [expandido, setExpandido] = useState(true);

    const API = `${config.API_URL}/api/colaciones-bot`;

    const cargarEstado = useCallback(async () => {
        try {
            const { data } = await Axios.get(`${API}/estado`, { params: { userId } });
            setEstadoBot(data);
        } catch (e) {
            setEstadoBot({ disponible: false, cutoff: '09:30' });
        }
    }, [API, userId]);

    const cargarDia = useCallback(async () => {
        if (!idEmpresa || !userId) return;
        setCargando(true);
        try {
            const { data } = await Axios.get(`${API}/dia`, { params: { fecha, idEmpresa, userId } });
            setDia(data);
        } catch (e) {
            console.error('Error cargando el día:', e);
            setDia(null);
        } finally {
            setCargando(false);
        }
    }, [API, fecha, idEmpresa, userId]);

    const cargarPendientes = useCallback(async () => {
        if (!userId) return;
        try {
            const { data } = await Axios.get(`${API}/pendientes`, { params: { userId } });
            setPendientes(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Error cargando pendientes:', e);
        }
    }, [API, userId]);

    useEffect(() => { cargarEstado(); }, [cargarEstado]);
    useEffect(() => { cargarDia(); }, [cargarDia]);
    useEffect(() => { cargarPendientes(); }, [cargarPendientes]);

    // Al abrir el módulo, si el día ya cerró, se sincroniza solo. Es idempotente,
    // así que no importa si el cron de las 09:35 ya lo hizo.
    useEffect(() => {
        if (dia && dia.cerrada && !dia.sincronizado && estadoBot.disponible) {
            sincronizar(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dia && dia.cerrada, dia && dia.sincronizado, estadoBot.disponible]);

    const sincronizar = async (silencioso = false) => {
        setSincronizando(true);
        try {
            const { data } = await Axios.post(`${API}/sync`, { desde: fecha, hasta: fecha, userId });

            if (!silencioso) {
                const lineas = [
                    `Votos leídos: ${data.votos}`,
                    `Colaciones creadas: ${data.colacionesCreadas}`,
                    `Colaciones actualizadas: ${data.colacionesActualizadas}`
                ];
                if (data.omitidasPorEdicionManual > 0) {
                    lineas.push(`Sin tocar por edición manual: ${data.omitidasPorEdicionManual}`);
                }
                if (data.pendientes > 0) {
                    lineas.push(`<strong class="text-warning">Pendientes de cruce: ${data.pendientes}</strong>`);
                }
                if (data.diasAbiertos.length > 0) {
                    lineas.push(`<em>No se guardó ${data.diasAbiertos.join(', ')}: la votación sigue abierta.</em>`);
                }
                Swal.fire({
                    title: 'Sincronización lista',
                    html: lineas.join('<br/>'),
                    icon: 'success'
                });
            }

            await Promise.all([cargarDia(), cargarPendientes()]);
            if (onCambio) onCambio();
        } catch (e) {
            const msg = e.response?.data?.error || e.message;
            if (!silencioso) {
                Swal.fire({
                    title: 'No se pudo sincronizar',
                    text: msg,
                    icon: e.response?.status === 503 ? 'warning' : 'error'
                });
            }
            console.error('Error sincronizando:', msg);
        } finally {
            setSincronizando(false);
        }
    };

    const resolver = async (payload) => {
        try {
            await Axios.post(`${API}/resolver`, { ...payload, userId, usuario });
            await Promise.all([cargarDia(), cargarPendientes()]);
            if (onCambio) onCambio();
        } catch (e) {
            Swal.fire('Error', e.response?.data?.error || e.message, 'error');
        }
    };

    const asignarTrabajador = async (voto) => {
        const opciones = trabajadores
            .map(t => `<option value="${t.ID_Trabajador}">${t.Nombres} ${t.Apellidos}</option>`)
            .join('');

        const { value, isConfirmed } = await Swal.fire({
            title: 'Asignar voto a un trabajador',
            html: `
                <div style="text-align:left;margin-bottom:12px;">
                    <div><strong>Nombre en WhatsApp:</strong> ${voto.NombreBot || '(sin nombre)'}</div>
                    <div><strong>Teléfono:</strong> ${voto.Telefono || voto.TelefonoRaw}</div>
                    <div><strong>Votó:</strong> ${voto.Opcion || '(sin opción)'}</div>
                </div>
                <select id="sel-trabajador" class="form-control">
                    <option value="">Seleccione un trabajador:</option>
                    ${opciones}
                </select>
                <label style="display:block;margin-top:12px;font-size:.9em;text-align:left;">
                    <input type="checkbox" id="chk-guardar" checked />
                    Guardar este teléfono en su ficha (para que cruce solo de aquí en adelante)
                </label>`,
            showCancelButton: true,
            confirmButtonText: 'Asignar',
            cancelButtonText: 'Cancelar',
            preConfirm: () => {
                const id = document.getElementById('sel-trabajador').value;
                if (!id) {
                    Swal.showValidationMessage('Debe seleccionar un trabajador');
                    return false;
                }
                return { idTrabajador: parseInt(id), guardarTelefono: document.getElementById('chk-guardar').checked };
            }
        });

        if (isConfirmed && value) {
            await resolver({ idVoto: voto.ID_Voto, accion: 'asignar', ...value });
        }
    };

    const registrarComoVisita = async (voto) => {
        const { value, isConfirmed } = await Swal.fire({
            title: 'Registrar como visita',
            input: 'text',
            inputLabel: 'Nombre de la visita',
            inputValue: voto.NombreBot || '',
            showCancelButton: true,
            confirmButtonText: 'Registrar',
            inputValidator: (v) => (!v ? 'Debe indicar un nombre' : undefined)
        });

        if (isConfirmed && value) {
            await resolver({ idVoto: voto.ID_Voto, accion: 'visita', nombreVisita: value });
        }
    };

    const ignorarVoto = async (voto) => {
        const { value, isConfirmed } = await Swal.fire({
            title: '¿Ignorar este voto?',
            text: 'Queda archivado y se puede reabrir después.',
            input: 'text',
            inputLabel: 'Motivo (obligatorio)',
            showCancelButton: true,
            confirmButtonText: 'Ignorar',
            inputValidator: (v) => (!v ? 'Debe indicar un motivo' : undefined)
        });

        if (isConfirmed && value) {
            await resolver({ idVoto: voto.ID_Voto, accion: 'ignorar', motivo: value });
        }
    };

    const etiquetaEstado = {
        sin_trabajador: { texto: 'No existe en la app', clase: 'badge-warning' },
        telefono_invalido: { texto: 'Teléfono ilegible', clase: 'badge-danger' },
        ambiguo: { texto: 'Más de un trabajador', clase: 'badge-danger' }
    };

    return (
        <div className="card border-left-primary shadow mb-4">
            <div className="card-header py-2 d-flex justify-content-between align-items-center">
                <h6 className="m-0 font-weight-bold text-primary">
                    <i className="fab fa-whatsapp mr-2"></i>
                    Votación por WhatsApp
                    {pendientes.length > 0 && (
                        <span className="badge badge-warning ml-2">
                            {pendientes.length} pendiente{pendientes.length === 1 ? '' : 's'} de cruce
                        </span>
                    )}
                </h6>
                <button
                    className="btn btn-sm btn-link"
                    onClick={() => setExpandido(!expandido)}
                >
                    <i className={`fas fa-chevron-${expandido ? 'up' : 'down'}`}></i>
                </button>
            </div>

            {expandido && (
                <div className="card-body">
                    <div className="row align-items-end mb-3">
                        <div className="col-md-3">
                            <label className="mb-1 text-xs font-weight-bold text-uppercase">Día</label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={fecha}
                                max={hoy}
                                onChange={(e) => setFecha(e.target.value)}
                            />
                        </div>

                        <div className="col-md-5">
                            {dia && (
                                dia.cerrada ? (
                                    dia.sincronizado ? (
                                        <span className="badge badge-success p-2">
                                            <i className="fas fa-lock mr-1"></i>
                                            Votación cerrada — dato definitivo
                                        </span>
                                    ) : (
                                        // Cerrada pero sin datos todavía no es lo mismo que
                                        // "no comió nadie": es que aún no llegaron.
                                        <span className="badge badge-secondary p-2">
                                            <i className="fas fa-hourglass-half mr-1"></i>
                                            Votación cerrada — sin datos del bot todavía
                                        </span>
                                    )
                                ) : (
                                    <span className="badge badge-warning p-2">
                                        <i className="fas fa-clock mr-1"></i>
                                        Votación abierta — cierra {dia.cutoff} · preliminar, no guardado
                                    </span>
                                )
                            )}
                            {estadoBot.disponible === false && (
                                <span className="badge badge-danger p-2 ml-2">
                                    <i className="fas fa-plug mr-1"></i>
                                    Bot sin responder — use la carga manual
                                </span>
                            )}
                        </div>

                        <div className="col-md-4 text-right">
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={() => sincronizar(false)}
                                disabled={sincronizando || (dia && !dia.cerrada) || estadoBot.disponible === false}
                                title={dia && !dia.cerrada
                                    ? `La votación cierra a las ${dia.cutoff}; antes de esa hora el dato no está completo`
                                    : 'Traer la votación del bot'}
                            >
                                <i className={`fas fa-${sincronizando ? 'spinner fa-spin' : 'sync'} mr-1`}></i>
                                {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
                            </button>
                        </div>
                    </div>

                    {cargando && <div className="text-center text-muted py-3">Cargando...</div>}

                    {/* Votación aún abierta: se muestra lo que hay, sin guardarlo. */}
                    {dia && !dia.cerrada && (
                        <div className="alert alert-warning py-2">
                            <strong>Preliminar.</strong> La votación cierra a las {dia.cutoff} y todavía
                            puede cambiar. Nada de esto se guarda hasta el cierre.
                            {dia.preliminar && dia.preliminar.length > 0 ? (
                                <ul className="mb-0 mt-2">
                                    {dia.preliminar.map((v, i) => (
                                        <li key={i}>
                                            {v.nombre || v.telefono} — {v.opcion}
                                            {!v.tipoColacion && <em className="text-muted"> (no almuerza)</em>}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="mt-2 text-muted">Sin votos todavía.</div>
                            )}
                        </div>
                    )}

                    {/* Votos que no cruzaron: nunca se descartan solos. */}
                    {pendientes.length > 0 && (
                        <div className="mb-4">
                            <h6 className="font-weight-bold text-warning">
                                <i className="fas fa-exclamation-triangle mr-1"></i>
                                Votos pendientes de cruce
                            </h6>
                            <p className="text-muted small mb-2">
                                Estas personas votaron pero el sistema no pudo determinar a qué trabajador
                                corresponden. Sus votos no se descartan: quedan aquí hasta que alguien los resuelva.
                            </p>
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered mb-0">
                                    <thead className="thead-light">
                                        <tr>
                                            <th>Día</th>
                                            <th>Nombre en WhatsApp</th>
                                            <th>Teléfono</th>
                                            <th>Votó</th>
                                            <th>Motivo</th>
                                            <th style={{ width: '220px' }}>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendientes.map(v => {
                                            const et = etiquetaEstado[v.EstadoCruce] || { texto: v.EstadoCruce, clase: 'badge-secondary' };
                                            return (
                                                <tr key={v.ID_Voto}>
                                                    <td>{new Date(v.Fecha).toLocaleDateString('es-CL')}</td>
                                                    <td>{v.NombreBot || <em className="text-muted">sin nombre</em>}</td>
                                                    <td><code>{v.Telefono || v.TelefonoRaw}</code></td>
                                                    <td>{v.Opcion || '—'}</td>
                                                    <td><span className={`badge ${et.clase}`}>{et.texto}</span></td>
                                                    <td>
                                                        <button className="btn btn-primary btn-sm mr-1"
                                                            onClick={() => asignarTrabajador(v)} title="Asignar a un trabajador">
                                                            <i className="fas fa-user-check"></i>
                                                        </button>
                                                        <button className="btn btn-info btn-sm mr-1"
                                                            onClick={() => registrarComoVisita(v)} title="Registrar como visita">
                                                            <i className="fas fa-user-plus"></i>
                                                        </button>
                                                        <button className="btn btn-secondary btn-sm"
                                                            onClick={() => ignorarVoto(v)} title="Ignorar (con motivo)">
                                                            <i className="fas fa-eye-slash"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Resultado del día ya cerrado. */}
                    {dia && dia.cerrada && !dia.sincronizado && (
                        <div className="alert alert-secondary py-2">
                            Todavía no llegaron los datos de este día.
                            {estadoBot.disponible === false
                                ? ' El bot no responde: registre las colaciones manualmente.'
                                : ' Use «Sincronizar» para traerlos.'}
                        </div>
                    )}

                    {dia && dia.cerrada && dia.sincronizado && (
                        <div className="row">
                            <div className="col-md-7">
                                <h6 className="font-weight-bold text-primary">
                                    Colaciones del día ({dia.registros.length})
                                </h6>
                                {dia.registros.length === 0 ? (
                                    <div className="alert alert-info py-2 mb-0">
                                        Sin registros para este día. Use «Sincronizar» o la carga manual.
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered mb-0">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>Persona</th>
                                                    <th>Tipo</th>
                                                    <th>Origen</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dia.registros.map(r => (
                                                    <tr key={r.ID_Colacion}>
                                                        <td>
                                                            {r.EsVisita === 1
                                                                ? <>{r.NombreVisita} <span className="badge badge-info">visita</span></>
                                                                : r.NombreTrabajador}
                                                        </td>
                                                        <td>{r.TipoColacion}</td>
                                                        <td>
                                                            <span className={`badge ${r.Origen === 'bot' ? 'badge-success' : 'badge-secondary'}`}>
                                                                {r.Origen === 'bot' ? 'WhatsApp' : 'Manual'}
                                                            </span>
                                                            {r.EditadoManual === 1 && (
                                                                <span className="badge badge-warning ml-1" title="Editado a mano: la sincronización ya no lo toca">
                                                                    editado
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className="col-md-5">
                                <h6 className="font-weight-bold text-secondary">
                                    Sin voto ({dia.sinVoto.length})
                                </h6>
                                <p className="text-muted small mb-2">
                                    Trabajadores con teléfono registrado que no votaron. No es lo mismo
                                    que haber votado «No almuerza».
                                </p>
                                {dia.sinVoto.length === 0 ? (
                                    <div className="alert alert-success py-2 mb-0">Votaron todos.</div>
                                ) : (
                                    <ul className="list-group list-group-flush">
                                        {dia.sinVoto.map(t => (
                                            <li key={t.ID_Trabajador}
                                                className="list-group-item py-1 px-2 d-flex justify-content-between align-items-center">
                                                <span className="small">{t.NombreTrabajador}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default ColacionesBot;
